/**
 * Autodesk viewer models keep coordinates in whatever unit the source file was
 * authored in (feet, metres, millimetres, ...). `model.getUnitScale()` returns how
 * many metres one model unit is, so every fixed real-world distance has to go
 * through it before it is added to a model coordinate.
 *
 * The BIM camera heights were originally tuned on feet models and hardcoded as raw
 * numbers, which put the camera ~5 m above the floor on metric models. Falling back
 * to feet when the scale is unknown keeps those models behaving exactly as before.
 */
export const METRES_PER_FOOT = 0.3048;

/** Height above the floor the 360 camera is assumed to be held at, in metres. */
export const LINKER_EYE_HEIGHT_M = 1.5;

/**
 * Distances the BIM camera placement was tuned with, in feet (the unit of the
 * models it was tuned on). Converted per model by `feetToModelUnits`.
 */
export const CAMERA_EYE_HEIGHT_FT = 5.0;
export const CAMERA_FLOOR_CLEARANCE_FT = 0.05;
export const CAMERA_FLOOR_SNAP_RANGE_FT = 20;

interface UnitScaledModel {
  getUnitScale?: () => number;
}

/** Metres per model unit; feet when the model does not report a usable scale. */
export function metresPerModelUnit(model: UnitScaledModel | null | undefined): number {
  const scale = model?.getUnitScale?.();
  return typeof scale === 'number' && Number.isFinite(scale) && scale > 0 ? scale : METRES_PER_FOOT;
}

export function metresToModelUnits(
  model: UnitScaledModel | null | undefined,
  metres: number,
): number {
  return metres / metresPerModelUnit(model);
}

export function feetToModelUnits(model: UnitScaledModel | null | undefined, feet: number): number {
  return metresToModelUnits(model, feet * METRES_PER_FOOT);
}
